/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useRef, useCallback } from 'react';
import { KeenIcon } from '@/components';
import { useSnackbar } from 'notistack';
import {
  useUploadGeoJsonFileMutation,
  useUploadExcelFileMutation,
} from '@/services/RoutingManagementService';

interface UploadRouteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUploadSuccess: () => void;
}

const UploadRouteModal: React.FC<UploadRouteModalProps> = ({
  isOpen,
  onClose,
  onUploadSuccess,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [source, setSource] = useState('');
  const [description, setDescription] = useState('');
  const [notes, setNotes] = useState('');
  const [mainContent, setMainContent] = useState(false);
  const [category, setCategory] = useState('VL5s');
  const [nameError, setNameError] = useState<string | null>(null);
  const [sourceError, setSourceError] = useState<string | null>(null);
  const [descriptionError, setDescriptionError] = useState<string | null>(null);
  const [notesError, setNotesError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadGeoJsonFile] = useUploadGeoJsonFileMutation();
  const [uploadExcelFile] = useUploadExcelFileMutation();

  const validFileTypes = [
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
    'application/geo+json', // .geojson
  ];

  const fileIcons: Record<string, string> = {
    geojson: '/media/file-types/geojson.png',
    xlsx: '/media/file-types/excel.svg',
    xls: '/media/file-types/excel.svg',
  };

  const validateFile = (file: File): any => {
    const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
    return (
      validFileTypes.includes(file.type) ||
      (extension && ['geojson', 'xlsx'].includes(extension))
    );
  };

  const isGeoJsonFile = (file: File): boolean => {
    const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
    return extension === 'geojson' || file.type === 'application/geo+json';
  };

  const isExcelFile = (file: File): boolean => {
    const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
    return (
      extension === 'xlsx' &&
      file.type ===
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    );
  };

  const handleFileChange = (file: File) => {
    if (validateFile(file)) {
      setSelectedFile(file);
      setError(null);
    } else {
      setSelectedFile(null);
      setError('Invalid file type. Please upload a .geojson or .xlsx file.');
    }
  };

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileChange(file);
    }
  }, []);

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleCancel = () => {
    setSelectedFile(null);
    setName('');
    setSource('');
    setDescription('');
    setNotes('');
    setCategory('VL5s');
    setMainContent(false);
    setError(null);
    setNameError(null);
    setSourceError(null);
    setDescriptionError(null);
    setNotesError(null);
    onClose();
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileChange(file);
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setName('');
    setSource('');
    setDescription('');
    setNotes('');
    setCategory('VL5s');
    setMainContent(false);
    setError(null);
    setNameError(null);
    setSourceError(null);
    setDescriptionError(null);
    setNotesError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async () => {
    if (!selectedFile) {
      setError('Please select a file to upload.');
      return;
    }

    let hasError = false;

    if (!name.trim()) {
      setNameError('Name is required.');
      hasError = true;
    } else {
      setNameError(null);
    }

    if (!source.trim()) {
      setSourceError('Source is required.');
      hasError = true;
    } else {
      setSourceError(null);
    }

    if (!description.trim()) {
      setDescriptionError('Description is required.');
      hasError = true;
    } else {
      setDescriptionError(null);
    }

    if (!notes.trim()) {
      setNotesError('Notes are required.');
      hasError = true;
    } else {
      setNotesError(null);
    }

    if (hasError) {
      return;
    }

    setIsUploading(true);
    setError(null);
    setNotesError(null);

    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('name', name.trim());
    formData.append('source', source.trim());
    formData.append('description', description.trim());
    formData.append('notes', notes.trim());
    formData.append('category', category);
    formData.append('main_content', mainContent.toString());

    try {
      if (isGeoJsonFile(selectedFile)) {
        await uploadGeoJsonFile(formData).unwrap();
      } else if (isExcelFile(selectedFile)) {
        await uploadExcelFile(formData).unwrap();
      } else {
        throw new Error(
          'Unsupported file type. Please upload a .geojson or Excel file.'
        );
      }
      enqueueSnackbar('File uploaded successfully', { variant: 'success' });
      onUploadSuccess();
      setSelectedFile(null);
      setNotes('');
      setError(null);
      setNotesError(null);
      onClose();
    } catch (err: any) {
      const errorMessage =
        err?.data?.message ||
        err.message ||
        'Failed to upload file. Please try again.';
      setError(errorMessage);
      enqueueSnackbar('Failed to upload file.', { variant: 'error' });
    } finally {
      setIsUploading(false);
    }
  };

  const handleSelectFileClick = () => {
    fileInputRef.current?.click();
  };

  const getFileIcon = (fileName: string) => {
    const extension = fileName.split('.').pop()?.toLowerCase() ?? '';
    return fileIcons[extension || 'geojson'] || fileIcons.geojson;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[999] flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-gray-100 rounded-lg shadow-xl w-full max-w-md m-4">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-800">
            Upload Route File
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 focus:outline-none"
            aria-label="Close modal"
          >
            <KeenIcon icon="cross" className="text-lg" />
          </button>
        </div>
        <div className="max-h-[400px] md:max-h-[500px] overflow-y-auto custom-scrollbar">
          <div className="p-6">
            {selectedFile ? (
              <div key="selected-file-content" className="space-y-4">
                <div className="flex items-center justify-between gap-2 p-4 bg-gray-50 rounded-lg dark:bg-black contrast:bg-black dark:text-white contrast:text-white">
                  <div className="flex items-center gap-2">
                    <img
                      src={getFileIcon(selectedFile.name)}
                      alt={`${selectedFile.name} icon`}
                      className="w-6 h-6 object-contain"
                      onError={(e) => {
                        e.currentTarget.src = fileIcons.geojson;
                      }}
                    />
                    <span className="text-sm text-gray-700 truncate max-w-[200px] dark:bg-black contrast:bg-black dark:text-white contrast:text-white px-2 py-1 rounded">
                      {selectedFile.name}
                    </span>
                  </div>
                  <button
                    onClick={handleRemoveFile}
                    className="text-red-500 hover:text-red-700 focus:outline-none"
                    aria-label={`Remove ${selectedFile.name}`}
                  >
                    <KeenIcon icon="cross-circle" className="text-lg" />
                  </button>
                </div>
                <div className="space-y-4">
                  <div>
                    <span className="block text-sm font-medium text-gray-700 mb-1">
                      Name *
                    </span>
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => {
                        setName(e.target.value);
                        if (e.target.value.trim()) {
                          setNameError(null);
                        }
                      }}
                      className={`input w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                        nameError ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Enter name"
                      aria-label="Name"
                      aria-required="true"
                      autoComplete="off"
                    />
                    {nameError && (
                      <div className="mt-1 text-red-600 text-sm">
                        {nameError}
                      </div>
                    )}
                  </div>

                  <div>
                    <span className="block text-sm font-medium text-gray-700 mb-1">
                      Source *
                    </span>
                    <input
                      type="text"
                      value={source}
                      onChange={(e) => {
                        setSource(e.target.value);
                        if (e.target.value.trim()) {
                          setSourceError(null);
                        }
                      }}
                      className={`input w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                        sourceError ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Enter source"
                      aria-label="Source"
                      aria-required="true"
                      autoComplete="off"
                    />
                    {sourceError && (
                      <div className="mt-1 text-red-600 text-sm">
                        {sourceError}
                      </div>
                    )}
                  </div>

                  <div>
                    <span className="block text-sm font-medium text-gray-700 mb-1">
                      Description *
                    </span>
                    <textarea
                      value={description}
                      onChange={(e) => {
                        setDescription(e.target.value);
                        if (e.target.value.trim()) {
                          setDescriptionError(null);
                        }
                      }}
                      className={`input w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 h-24 resize-none ${
                        descriptionError ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Enter description"
                      aria-label="Description"
                      aria-required="true"
                    />
                    {descriptionError && (
                      <div className="mt-1 text-red-600 text-sm">
                        {descriptionError}
                      </div>
                    )}
                  </div>

                  <div>
                    <span className="block text-sm font-medium text-gray-700 mb-1">
                      Notes *
                    </span>
                    <textarea
                      value={notes}
                      onChange={(e) => {
                        setNotes(e.target.value);
                        if (e.target.value.trim()) {
                          setNotesError(null);
                        }
                      }}
                      className={`input w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 h-24 resize-none ${
                        notesError ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Enter notes"
                      aria-label="Notes"
                      aria-required="true"
                    />
                    {notesError && (
                      <div className="mt-1 text-red-600 text-sm flex items-center gap-2">
                        <span>{notesError}</span>
                      </div>
                    )}
                  </div>

                  <div>
                    <span className="block text-sm font-medium text-gray-700 mb-1">
                      Category
                    </span>
                    <select
                      value={category}
                      onChange={(e) => setCategory(e.target.value)}
                      className="w-full p-2 bg-gray-200 border rounded-lg focus:ring-2 focus:ring-blue-500 border-gray-300"
                      aria-label="Category"
                    >
                      <option value="VL5s">
                        VL5s: Local boating fairway (private)
                      </option>
                      <option value="VL6s">
                        VL6s: Boating route (private)
                      </option>
                      <option value="VL7s">
                        VL7s: Recreational boating route (private)
                      </option>
                    </select>
                  </div>

                  <div>
                    <span className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={mainContent}
                        onChange={(e) => setMainContent(e.target.checked)}
                        className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2 cursor-pointer"
                        aria-label="Main Content"
                      />
                      <span className="text-sm font-medium text-gray-700">
                        Main Content
                      </span>
                    </span>
                    <div className="mt-1 text-xs text-gray-500 ml-6">
                      On selecting it will be included in official fairway and
                      not be displayed with semiofficial fairways
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div
                className={`border-2 border-dashed rounded-lg p-6 text-center ${
                  isDragging
                    ? 'border-blue-500 bg-blue-100 dark:bg-blue-900 contrast:bg-blue-900/20'
                    : 'border-gray-300 bg-gray-100'
                }`}
                role="region"
                aria-label="File drop zone; drag and drop files here"
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
              >
                <KeenIcon
                  icon="cloud-upload"
                  className={`text-4xl mx-auto mb-4 ${
                    isDragging ? 'text-blue-500' : 'text-gray-500'
                  }`}
                />
                <p className="text-gray-600 mb-2">
                  Drag and drop a GeoJSON or Excel file here
                </p>
                <p className="text-sm text-gray-500 mb-4">
                  Supported formats: .geojson, .xlsx
                </p>
                <button
                  type="button"
                  onClick={handleSelectFileClick}
                  className="btn btn-sm btn-light border border-gray-300 hover:bg-gray-100"
                  aria-label="Select file to upload"
                >
                  Select File
                </button>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileInputChange}
                  accept=".geojson,.xlsx"
                  className="hidden"
                  aria-label="Upload GeoJSON or Excel file"
                />
              </div>
            )}
            {error && (
              <div className="mt-4 text-red-600 text-sm flex items-center gap-2">
                <span>{error}</span>
              </div>
            )}
            {isUploading && (
              <div className="mt-4 text-gray-600 text-sm flex items-center gap-2">
                <KeenIcon icon="spinner" className="animate-spin text-lg" />
                <span>Uploading...</span>
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2 p-4 border-t border-gray-200">
            <button
              onClick={handleCancel}
              className="btn btn-sm btn-light"
              aria-label="Cancel upload"
              disabled={isUploading}
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              className="btn btn-sm btn-primary"
              disabled={!selectedFile || isUploading}
              aria-label="Upload selected file"
            >
              Upload
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UploadRouteModal;
